import { useState, useEffect } from 'react'
import { ScrollReveal } from '@/components/ui/scroll-reveal'
import { AspectRatio } from '@/components/ui/aspect-ratio'
import { getSiteSettings, SiteSettings } from '@/services/content'
import useLanguageStore from '@/stores/languageStore'
import { Loader2, PlayCircle } from 'lucide-react'

export const VideoSection = () => {
  const { currentLocale } = useLanguageStore()
  const [settings, setSettings] = useState<SiteSettings | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true)
      const { data } = await getSiteSettings(currentLocale)
      if (data) setSettings(data)
      setLoading(false)
    }
    fetchSettings()
  }, [currentLocale])

  if (loading) {
    return null
  }

  // If no video URL is configured, don't render the section
  if (!settings?.explainer_video_url) {
    return null
  }

  // Basic function to convert YouTube watch URLs to embed URLs if needed
  const getEmbedUrl = (url: string) => {
    if (!url) return ''
    if (url.includes('youtube.com/watch?v=')) {
      return url.replace('watch?v=', 'embed/')
    }
    if (url.includes('youtu.be/')) {
      const id = url.split('youtu.be/')[1]
      return `https://www.youtube.com/embed/${id}`
    }
    // Return original if it seems fine or is another provider (vimeo etc)
    return url
  }

  const embedUrl = getEmbedUrl(settings.explainer_video_url)
  const title =
    settings.explainer_video_title ||
    (currentLocale === 'pt' ? 'Como Funciona' : 'How It Works')

  return (
    <section className="py-24 bg-slate-900 text-white overflow-hidden">
      <div className="container px-4 md:px-6 max-w-5xl mx-auto">
        <ScrollReveal className="text-center mb-12">
          <div className="inline-flex items-center justify-center space-x-2 mb-6 px-4 py-1.5 rounded-full bg-white/10 text-white font-medium text-sm backdrop-blur-sm border border-white/20">
            <PlayCircle size={16} />
            <span>Video</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">{title}</h2>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-slate-800">
            <AspectRatio ratio={16 / 9}>
              <iframe
                src={embedUrl}
                title={title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </AspectRatio>
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
}
